import React, { useState, useEffect } from "react";
import { Grid, TextField, Typography, Alert, Box, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { getEquipmentById, updateEquipment } from "../services/userEquipmentService";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

const EquipmentEdit = ({ equipmentId, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    type: "",
    brand: "",
    model: "",
    serial_number: "",
    color: "",
    description: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentImagePath, setCurrentImagePath] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const equipmentTypes = [
    { value: "computador", label: "Computador" },
    { value: "tableta", label: "Tableta" },
    { value: "otros", label: "Otros" },
  ];

  useEffect(() => {
    const loadEquipment = async () => {
      try {
        setLoading(true);
        const result = await getEquipmentById(equipmentId);
        if (result.success) {
          const equipment = result.data;
          setFormData({
            type: equipment.type,
            brand: equipment.brand,
            model: equipment.model,
            serial_number: equipment.serial_number,
            color: equipment.color,
            description: equipment.description || "",
          });
          setCurrentImagePath(equipment.image_path);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (equipmentId) {
      loadEquipment();
    }
  }, [equipmentId]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      setError("");

      const result = await updateEquipment(equipmentId, formData, imageFile);

      if (result.success) {
        onSuccess(result.data);
      } else {
        setError(result.message || "Error al actualizar equipo");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.type) {
    return (
      <MDBox p={3}>
        <MDTypography variant="body2">Cargando datos del equipo...</MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={2}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Tipo de equipo"
            value={formData.type}
            onChange={(e) => handleInputChange("type", e.target.value)}
            fullWidth
            required
            sx={{
              "& .MuiInputBase-root": {
                height: "45px",
              },
              "& .MuiSelect-select": {
                padding: "12px 14px",
              },
            }}
          >
            {equipmentTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Marca"
            value={formData.brand}
            onChange={(e) => handleInputChange("brand", e.target.value)}
            fullWidth
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Modelo"
            value={formData.model}
            onChange={(e) => handleInputChange("model", e.target.value)}
            fullWidth
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Número de Serie"
            value={formData.serial_number}
            onChange={(e) => handleInputChange("serial_number", e.target.value)}
            fullWidth
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Color"
            value={formData.color}
            onChange={(e) => handleInputChange("color", e.target.value)}
            fullWidth
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Descripción"
            placeholder="Descripción adicional del equipo (opcional)"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setImageFile(file);
                    const reader = new FileReader();
                    reader.onload = (e) => setImagePreview(e.target.result);
                    reader.readAsDataURL(file);
                  }
                }}
                style={{ flex: 1, minWidth: 0, width: "100%" }}
              />
              <Box
                sx={{
                  bgcolor: "white",
                  borderRadius: "50%",
                  p: 0.6,
                  boxShadow: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  flexShrink: 0,
                }}
                onClick={async () => {
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({
                      video: { facingMode: "environment" },
                    });

                    const video = document.createElement("video");
                    video.srcObject = stream;
                    video.play();

                    const modal = document.createElement("div");
                    modal.style.cssText =
                      "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.9);z-index:9999;display:flex;align-items:center;justify-content:center;flex-direction:column";

                    video.style.cssText = "max-width:85%;max-height:60%;border-radius:8px";

                    const captureBtn = document.createElement("button");
                    captureBtn.textContent = "Capturar";
                    captureBtn.style.cssText =
                      "margin:10px;padding:10px 20px;background:#1976d2;color:white;border:none;border-radius:5px;cursor:pointer;font-size:16px";

                    const closeBtn = document.createElement("button");
                    closeBtn.textContent = "Cerrar";
                    closeBtn.style.cssText =
                      "margin:10px;padding:10px 20px;background:#f44336;color:white;border:none;border-radius:5px;cursor:pointer;font-size:16px";

                    modal.appendChild(video);
                    modal.appendChild(captureBtn);
                    modal.appendChild(closeBtn);
                    document.body.appendChild(modal);

                    captureBtn.onclick = () => {
                      const canvas = document.createElement("canvas");
                      canvas.width = video.videoWidth;
                      canvas.height = video.videoHeight;
                      canvas.getContext("2d").drawImage(video, 0, 0);

                      canvas.toBlob(
                        (blob) => {
                          const file = new File([blob], "camera-capture.jpg", {
                            type: "image/jpeg",
                          });
                          setImageFile(file);
                          const reader = new FileReader();
                          reader.onload = (e) => setImagePreview(e.target.result);
                          reader.readAsDataURL(file);
                          stream.getTracks().forEach((track) => track.stop());
                          document.body.removeChild(modal);
                        },
                        "image/jpeg",
                        0.8
                      );
                    };

                    closeBtn.onclick = () => {
                      stream.getTracks().forEach((track) => track.stop());
                      document.body.removeChild(modal);
                    };
                  } catch (error) {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.capture = "environment";
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setImageFile(file);
                        const reader = new FileReader();
                        reader.onload = (e) => setImagePreview(e.target.result);
                        reader.readAsDataURL(file);
                      }
                    };
                    input.click();
                  }
                }}
                title="Tomar nueva foto"
              >
                <CameraAltIcon sx={{ color: "#1976d2", fontSize: 28 }} />
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="textSecondary" display="block">
            Nueva imagen (opcional) - Máximo 2MB
          </Typography>
          {imageFile && (
            <>
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                ✓ Nueva imagen seleccionada: {imageFile.name}
              </Typography>
              {imagePreview && (
                <Box sx={{ mt: 1, textAlign: "center" }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "100px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setModalImageSrc(imagePreview);
                      setShowImageModal(true);
                    }}
                    title="Click para ver imagen completa"
                  />
                </Box>
              )}
            </>
          )}
          {currentImagePath && !imageFile && (
            <>
              <Typography variant="caption" color="info.main" display="block" sx={{ mt: 1 }}>
                📷 Imagen actual:
              </Typography>
              <Box sx={{ mt: 1, textAlign: "center" }}>
                <img
                  src={`${process.env.REACT_APP_BASE_URL}storage/${currentImagePath}`}
                  alt="Imagen actual"
                  style={{
                    maxWidth: "150px",
                    maxHeight: "100px",
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setModalImageSrc(
                      `${process.env.REACT_APP_BASE_URL}storage/${currentImagePath}`
                    );
                    setShowImageModal(true);
                  }}
                  title="Click para ver imagen completa"
                />
              </Box>
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 2,
              mt: 2,
            }}
          >
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={onCancel}
              sx={{ order: { xs: 2, sm: 1 } }}
            >
              Cancelar
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleUpdate}
              disabled={
                loading ||
                !formData.type ||
                !formData.brand ||
                !formData.model ||
                !formData.serial_number ||
                !formData.color
              }
              sx={{ order: { xs: 1, sm: 2 } }}
            >
              {loading ? "Actualizando..." : "Actualizar Equipo"}
            </MDButton>
          </Box>
        </Grid>
      </Grid>

      {/* Modal para ver imagen completa */}
      {showImageModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.9)",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          onClick={() => setShowImageModal(false)}
        >
          <button
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "rgba(255,255,255,0.9)",
              border: "none",
              borderRadius: "50%",
              width: "35px",
              height: "35px",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10001,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowImageModal(false);
            }}
          >
            ×
          </button>
          <img
            src={modalImageSrc}
            alt="Imagen completa"
            style={{
              maxWidth: "85%",
              maxHeight: "70%",
              objectFit: "contain",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </MDBox>
  );
};

export default EquipmentEdit;
